import { AlertTriangle, CheckCircle2, Clock, Info } from "lucide-react";

export const NAVY = "#1B4F8A";

// ``group`` renders a div instead of a label: needed when the field
// holds buttons or a canvas, otherwise a click on the caption would
// activate the first button inside (label activation behaviour).
export function Field({ label, hint, required, children, testid, group = false, className = "", invalid = false }) {
  const Tag = group ? "div" : "label";
  return (
    <Tag className={`flex flex-col gap-2 ${className} ${invalid ? "[&_input]:border-red-500 [&_select]:border-red-500 [&_textarea]:border-red-500 [&_input]:ring-4 [&_input]:ring-red-500/10" : ""}`} data-testid={testid} data-invalid={invalid ? "true" : undefined}>
      <span className={`flex items-baseline gap-1.5 text-[13px] font-medium leading-snug ${invalid ? "text-red-700" : "text-stone-700"}`}>
        {label}
        {required && <span className={`inline-block w-1.5 h-1.5 rounded-full translate-y-[-1px] ${invalid ? "bg-red-500" : "bg-[#3EA8DC]"}`} aria-label="required" />}
        {invalid && <span className="text-xs font-normal text-red-600" data-testid={testid ? `${testid}-missing` : undefined}>Please complete</span>}
      </span>
      {children}
      {hint && <span className="text-xs text-stone-500 leading-relaxed">{hint}</span>}
    </Tag>
  );
}

const inputCls =
  "h-11 w-full rounded-md border border-stone-300 bg-white px-3.5 text-[15px] text-stone-900 placeholder:text-stone-400 shadow-[inset_0_1px_2px_rgba(0,0,0,0.03)] transition-[border-color,box-shadow] focus:outline-none focus:border-[#1B4F8A] focus:ring-4 focus:ring-[#1B4F8A]/10";

export function TextInput({ value, onChange, type = "text", placeholder, testid, ...rest }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={inputCls}
      data-testid={testid}
      {...rest}
    />
  );
}

export function TextArea({ value, onChange, rows = 3, placeholder, testid }) {
  return (
    <textarea
      rows={rows}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-md border border-stone-300 bg-white px-3.5 py-3 text-[15px] leading-relaxed text-stone-900 placeholder:text-stone-400 shadow-[inset_0_1px_2px_rgba(0,0,0,0.03)] transition-[border-color,box-shadow] focus:outline-none focus:border-[#1B4F8A] focus:ring-4 focus:ring-[#1B4F8A]/10"
      data-testid={testid}
    />
  );
}

export function Select({ value, onChange, options, testid, placeholder = "Please choose" }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputCls} ${value ? "" : "text-stone-400"}`}
      data-testid={testid}
    >
      <option value="">{placeholder}</option>
      {options.map(([v, label]) => (
        <option key={v} value={v} className="text-stone-900">{label}</option>
      ))}
    </select>
  );
}

export function Choice({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map(([v, label]) => {
        const active = value === v;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            aria-pressed={active}
            data-testid={testid ? `${testid}-${v}` : undefined}
            className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-[background-color,border-color,color,transform] active:scale-[0.98] ${
              active
                ? "bg-[#1B4F8A] text-white border-[#1B4F8A] shadow-[0_6px_16px_-8px_rgba(27,79,138,0.8)]"
                : "bg-white text-stone-700 border-stone-300 hover:border-[#1B4F8A] hover:text-[#1B4F8A]"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export const YES_NO = [["yes", "Yes"], ["no", "No"]];

// Numbered group of fields, styled like the viewer's section heads:
// tracked mono number in sky, serif title, hairline rule.
export function Section({ n, title, intro, children, testid, tone = "paper" }) {
  const tones = {
    paper: "bg-white border-stone-200",
    dark: "bg-[#0B1220] border-[#0B1220] text-stone-100",
    signing: "bg-stone-50 border-stone-300 border-dashed border-2",
  };
  return (
    <section className={`rounded-xl border p-6 sm:p-8 flex flex-col gap-6 scroll-mt-24 ${tones[tone]}`} data-testid={testid}>
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline gap-3 sm:gap-4">
          {n && <span className={`font-mono text-xs tracking-[0.2em] ${tone === "dark" ? "text-[#3EA8DC]" : "text-[#3EA8DC]"}`}>{n}</span>}
          <h3 className={`font-serif font-light text-2xl sm:text-[1.75rem] leading-tight ${tone === "dark" ? "text-white" : "text-stone-900"}`}>{title}</h3>
          <div className={`flex-1 h-px ${tone === "dark" ? "bg-white/10" : "bg-stone-200"}`} />
        </div>
        {intro && <p className={`text-[15px] leading-relaxed ${tone === "dark" ? "text-stone-300" : "text-stone-500"}`}>{intro}</p>}
      </div>
      {children}
    </section>
  );
}

// Step header above a group of sections: "Step 3 of 5" eyebrow, big
// serif title and a short introduction.
export function StepHeader({ eyebrow, title, intro, aside }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div className="max-w-2xl">
        <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#1B4F8A]">{eyebrow}</p>
        <h2 className="font-serif font-light text-3xl sm:text-4xl text-stone-900 leading-tight mt-2">{title}</h2>
        {intro && <p className="text-[15px] sm:text-base text-stone-600 leading-relaxed mt-3">{intro}</p>}
      </div>
      {aside && <div className="shrink-0">{aside}</div>}
    </div>
  );
}

const TONES = {
  warning: ["border-amber-200 bg-amber-50 text-amber-900", Clock],
  danger: ["border-rose-200 bg-rose-50 text-rose-900", AlertTriangle],
  success: ["border-emerald-200 bg-emerald-50 text-emerald-900", CheckCircle2],
  info: ["border-sky-200 bg-sky-50 text-sky-900", Info],
};

export function Notice({ tone = "info", title, children, testid }) {
  const [cls, Icon] = TONES[tone];
  return (
    <div className={`flex gap-3 rounded-lg border px-4 py-3.5 text-sm leading-relaxed ${cls}`} data-testid={testid} role={tone === "danger" ? "alert" : undefined}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex flex-col gap-0.5">
        {title && <p className="font-semibold">{title}</p>}
        <div>{children}</div>
      </div>
    </div>
  );
}

export function PrimaryButton({ children, onClick, disabled, testid, type = "button", size = "md", className = "" }) {
  const sizes = { md: "px-6 py-3 text-[15px]", lg: "px-7 py-3.5 text-base w-full sm:w-auto" };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 rounded-md bg-[#1B4F8A] text-white font-semibold shadow-[0_10px_24px_-12px_rgba(27,79,138,0.9)] transition-[background-color,transform,box-shadow] hover:bg-[#143E72] hover:-translate-y-px active:translate-y-0 disabled:opacity-40 disabled:hover:translate-y-0 disabled:shadow-none ${sizes[size]} ${className}`}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, onClick, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1B4F8A] hover:text-[#143E72] underline underline-offset-4 decoration-[#1B4F8A]/30 hover:decoration-[#143E72] disabled:opacity-50 transition-colors"
    >
      {children}
    </button>
  );
}

export function Stat({ label, value, mono = true, testid, tone = "paper" }) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <dt className={`font-mono text-[10px] tracking-[0.18em] uppercase ${tone === "dark" ? "text-stone-400" : "text-stone-500"}`}>{label}</dt>
      <dd className={`${mono ? "font-mono tabular-nums" : ""} text-[15px] sm:text-base truncate ${tone === "dark" ? "text-white" : "text-stone-900"}`} data-testid={testid}>{value}</dd>
    </div>
  );
}
